import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppSelector } from "../../store/hook";
import ReactDatePicker from "react-datepicker";
import moment from "moment";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

// Local imports
import { formattedDate } from "../data";
import RankingsSection from "./RankingsSection";
import ReviewsSection from "./ReviewsSection";
import AnalyticsSection from "./AnalyticsSection";
import EmailCampaignsSection from "./EmailCampaignsSection";
import AcquisitionSection from "./AcquisitionSection";
import LostLeadsSection from "./LostLeadsSection";
import NewPatientsSection from "./NewPatientsSection";
import ObservationsRecommendationsSection from "./ObservationsRecommendationsSection";
import { DatePicker } from "antd";
import PatientFollowUp from "./PatientFollowUp";
import PatientCallBack from "./patientCallBack";
import logo from "../../logo.svg";
import { CircularProgress } from "@mui/material";
import { styled } from "@mui/material/styles";

declare module "jspdf" {
  interface jsPDF {
    saveGraphicsState: () => jsPDF;
    restoreGraphicsState: () => jsPDF;
    setFont: (
      fontName: string,
      fontStyle?: string,
      fontWeight?: string | number
    ) => jsPDF;
    setFontSize: (size: number) => jsPDF;
    setTextColor: {
      (ch1: string): jsPDF;
      (ch1: number): jsPDF;
      (ch1: number, ch2: number, ch3: number, ch4?: number): jsPDF;
    };
    setDrawColor: {
      (ch1: string): jsPDF;
      (ch1: number): jsPDF;
      (ch1: number, ch2: number, ch3: number, ch4?: number): jsPDF;
    };
    setLineWidth: (width: number) => jsPDF;
    line: (
      x1: number,
      y1: number,
      x2: number,
      y2: number,
      style?: string | null
    ) => jsPDF;
  }
}

declare global {
  interface Window {
    isPrintMode?: boolean;
  }
}

interface Props {
  // clinicUniqueId: string;
}

const ClientReport = ({}: Props) => {
  const { RangePicker } = DatePicker;
  const [lastUsedDatePicker, setLastUsedDatePicker] = useState<
    "month" | "range" | null
  >(null);
  const clinics = useAppSelector((state) => state.user.user.clinics);
  const [searchParams] = useSearchParams();
  const month = useMemo(() => {
    let retLabel = 0;
    switch (searchParams.get("filter")) {
      case "thismonths":
        retLabel = 0;
        break;
      case "1months":
        retLabel = 1;
        break;
      case "3months":
        retLabel = 3;
        break;
      case "6months":
        retLabel = 6;
        break;
      case "9months":
        retLabel = 9;
        break;
      case "12months":
        retLabel = 12;
        break;
    }
    return retLabel;
  }, [searchParams]);
  const [dateFilter, setDateFilter] = useState({
    startDate: month
      ? moment().subtract(month, "months").startOf("month").format("YYYY-MM-DD")
      : moment().startOf("month").format("YYYY-MM-DD"),
    endDate: month
      ? moment().subtract(1, "month").endOf("month").format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD"),
  });
  const [selectedClinic, setSelectedClinic] = useState(
    searchParams.get("clinic")
      ? searchParams.get("clinic")
      : sessionStorage.getItem("selectedClinic") &&
        sessionStorage.getItem("selectedClinic") !== ""
      ? sessionStorage.getItem("selectedClinic")
      : clinics?.[0]?.uniqueId
  );
  const clinicData = clinics?.find(
    (item: any) => item.uniqueId === selectedClinic
  );

  // Handle clinic changes
  useEffect(() => {
    if (selectedClinic && clinics) {
      const clinic = clinics.find(
        (item: any) => item.uniqueId === selectedClinic
      );
      if (!clinic) {
        // If selected clinic is not found, select the first clinic
        setSelectedClinic(clinics[0].uniqueId);
      }
    }
  }, [clinics, selectedClinic]);

  // Update URL when clinic changes
  useEffect(() => {
    if (selectedClinic) {
      const params = new URLSearchParams(window.location.search);
      params.set("clinic", selectedClinic);
      const newRelativePathQuery = `${
        window.location.pathname
      }?${params.toString()}`;
      window.history.pushState({}, "", newRelativePathQuery);
    }
  }, [selectedClinic]);
  const [selectedQuater, setSelectedQuater] = useState(
    searchParams.get("filter")
      ? searchParams.get("filter") ?? "thismonths"
      : "thismonths"
  );
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isPrintMode, setIsPrintMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const dashboardRef = useRef<HTMLDivElement>(null);
  const addPdfHeader = (pdf: jsPDF, clinicName: string, dateRange: string) => {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const headerHeight = 10; // thinner header
    const margin = 10;

    pdf.saveGraphicsState();

    pdf.setFillColor(29, 43, 54);
    pdf.rect(0, 0, pageWidth, headerHeight, "F");

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.setTextColor(255, 255, 255);
    const clinicText = clinicName || "Clinic";
    const textWidthClinic =
      (pdf.getStringUnitWidth(clinicText) * pdf.getFontSize()) /
      pdf.internal.scaleFactor;
    const textXClinic = (pageWidth - textWidthClinic) / 2;
    const textYClinic = headerHeight / 2 + 2.5;
    pdf.text(clinicText, textXClinic, textYClinic);

    pdf.setFontSize(7);
    pdf.text(dateRange, pageWidth - margin, textYClinic, { align: "right" });

    pdf.restoreGraphicsState();
    return headerHeight + 3;
  };

  const addPdfFooter = (pdf: jsPDF, pageNum: number, totalPages: number) => {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const footerHeight = 10;

    pdf.saveGraphicsState();

    pdf.setFillColor(243, 244, 246);
    pdf.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, "F");

    pdf.setTextColor(25, 78, 91);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);

    const pageText = `Page ${pageNum} of ${totalPages}`;
    pdf.text(pageText, pageWidth - margin, pageHeight - 4, { align: "right" });

    const copyrightText = `© ${new Date().getFullYear()} Xcare Managed Marketing Report`;
    pdf.text(copyrightText, margin, pageHeight - 4);

    pdf.restoreGraphicsState();
  };

  const addWatermark = (pdf: jsPDF) => {
    const pageCount = pdf.getNumberOfPages();
    const lines = ["Xcare"];

    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Save the current graphics state
      pdf.saveGraphicsState();

      // Set watermark properties
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(28); // Slightly smaller font for two lines
      pdf.setTextColor(200, 200, 200, 0.3);

      // Save state before rotation
      pdf.saveGraphicsState();

      // Set opacity
      pdf.setGState(new (pdf as any).GState({ opacity: 0.1 }));

      // Move to center
      const centerX = pageWidth / 2;
      const centerY = pageHeight / 2;

      // Calculate total height of both lines
      const lineHeight = 35; // Space between lines

      // Draw each line of text
      lines.forEach((line, index) => {
        pdf.text(line, centerX, centerY - lineHeight / 2 + index * lineHeight, {
          align: "center",
          angle: 45,
        });
      });

      // Restore the graphics state
      pdf.restoreGraphicsState();
    }
  };

  // Loading Modal Component
  const LoadingModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex flex-col items-center">
          {/* <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-l-2 border-[#194E5B] mb-4"></div> */}
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Generating PDF...
          </h3>
          <p className="text-gray-600 text-center">
            Please wait while we prepare your report. This may take a moment...
          </p>
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    if (clinics === undefined || clinics === null) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [clinics]);

  useEffect(() => {
    // Initial check (set by server.js script injection)
    if (window.isPrintMode) {
      setIsPrintMode(true);
    }

    // Listen for the custom print-mode-check event from our injected script
    const handlePrintModeCheck = (event: Event) => {
      const customEvent = event as CustomEvent;
      const isPrintMode = customEvent.detail?.isPrintMode || false;
      console.log("Received print mode event:", isPrintMode);
      setIsPrintMode(isPrintMode);
    };

    window.addEventListener("print-mode-check", handlePrintModeCheck);

    // Apply print-specific classes when print mode is detected
    if (isPrintMode && dashboardRef.current) {
      dashboardRef.current.classList.add("printable-dashboard");

      // Add page break classes to sections for better printing
      const sections = dashboardRef.current.querySelectorAll(".section");
      sections.forEach((section: Element, index: number) => {
        if (index > 0) {
          section.classList.add("page-break-before");
        }
      });
    }

    return () => {
      window.removeEventListener("print-mode-check", handlePrintModeCheck);
    };
  }, [isPrintMode]);

  useEffect(() => {
    if (dashboardRef.current) {
      if (isGeneratingPDF) {
        dashboardRef.current.classList.add("pdf-generating");
      } else {
        dashboardRef.current.classList.remove("pdf-generating");
      }
    }
  }, [isGeneratingPDF]);

  const generatePDF = async () => {
    if (!dashboardRef.current) return;

    setIsGeneratingPDF(true);
    setIsGenerating(true);

    const dateRange = `From ${dateFilter?.startDate} to ${dateFilter?.endDate}`;

    try {
      const currentDate = new Date();
      const month = String(currentDate.getMonth() + 1).padStart(2, "0");
      const year = currentDate.getFullYear();
      const pdfFileName = `${
        clinicData?.name || "clinic"
      }_${month}_${year}.pdf`;

      const sectionNodes = dashboardRef.current.querySelectorAll(".section");
      if (!sectionNodes.length) {
        alert("No sections found to export.");
        setIsGeneratingPDF(false);
        return;
      }

      window.scrollTo(0, 0);
      await new Promise((res) => setTimeout(res, 500));

      const tempStyle = document.createElement("style");
      tempStyle.textContent = `
      .pdf-generating * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        -webkit-transform: translateZ(0);
        transform: translateZ(0);
      }
      .pdf-generating {
        font-smooth: never;
        -webkit-font-smoothing: subpixel-antialiased;
        -moz-osx-font-smoothing: auto;
      }
      .pdf-download-button { display: none !important; }
    `;
      document.head.appendChild(tempStyle);

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      let isFirstPage = true;

      for (let i = 0; i < sectionNodes.length; i++) {
        const section = sectionNodes[i] as HTMLElement;

        section.classList.add("pdf-generating");
        await new Promise((res) => setTimeout(res, 100));

        const sectionCanvas = await html2canvas(section, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          logging: false,
          imageTimeout: 0,
          removeContainer: true,
          width: section.scrollWidth,
          height: section.scrollHeight,
          x: 0,
          y: 0,
        });

        section.classList.remove("pdf-generating");

        const imgWidth = pdfWidth;
        const imgHeight =
          (sectionCanvas.height * imgWidth) / sectionCanvas.width;

        let position = 0;
        let pageNum = 0;
        const pageHeightPx = Math.floor(
          (pdfHeight * sectionCanvas.width) / imgWidth
        );

        while (position < sectionCanvas.height) {
          const remainingHeight = sectionCanvas.height - position;
          const pageCanvasHeight = Math.min(pageHeightPx, remainingHeight);

          const pageCanvas = document.createElement("canvas");
          pageCanvas.width = sectionCanvas.width;
          pageCanvas.height = pageCanvasHeight;
          const ctx = pageCanvas.getContext("2d");

          if (ctx) {
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
            ctx.drawImage(
              sectionCanvas,
              0,
              position,
              sectionCanvas.width,
              pageCanvasHeight,
              0,
              0,
              sectionCanvas.width,
              pageCanvasHeight
            );
          }

          const pageImgData = pageCanvas.toDataURL("image/png");

          // Add new page if not first
          if (!isFirstPage) pdf.addPage();

          // Add header only from second page onward
          let headerOffset = 0;
          if (!isFirstPage) {
            headerOffset = addPdfHeader(
              pdf,
              clinicData?.name || "Clinic",
              dateRange
            );
          }

          const footerHeight = 10;
          const footerMargin = 5;
          const availableHeight =
            pdfHeight - headerOffset - footerHeight - footerMargin;

          const imgDisplayHeight = Math.min(
            (pageCanvasHeight * imgWidth) / sectionCanvas.width,
            availableHeight
          );

          pdf.addImage(
            pageImgData,
            "PNG",
            0,
            headerOffset,
            imgWidth,
            imgDisplayHeight
          );

          const totalPages = pdf.getNumberOfPages();
          addPdfFooter(pdf, totalPages, totalPages);

          position += pageCanvasHeight;
          isFirstPage = false; // after first page
          pageNum++;
        }
      }

      document.head.removeChild(tempStyle);
      addWatermark(pdf);
      pdf.save(pdfFileName);
    } catch (error) {
      console.error("PDF generation error:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setIsGeneratingPDF(false);
      setIsGenerating(false);
    }
  };

  const LoadingSpinner = styled(CircularProgress)({
    color: "#4CC9C0",
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  });

  return (
    <div className="bg-[#F8F9FA] relative" ref={dashboardRef}>
      {isGenerating && <LoadingModal />}
      {isLoading ? (
        <div className="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center">
          <div className="col-span-2">
            <LoadingSpinner />
          </div>
          {/* <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-l-2 border-[#194E5B]"></div> */}
        </div>
      ) : (
        <>
          <main className="container mx-auto px-2 py-6">
            <div className="dashboard-grid grid grid-cols-1 gap-6">
              <div className="section">
                <header className="bg-white shadow-sm mb-4 rounded-md w-full">
                  <div className="container mx-auto px-1">
                    <div className="flex justify-between items-center py-4">
                      <div
                        className={`header-right flex justify-between ${
                          isGeneratingPDF ? "items-center" : "items-start"
                        } gap-2 w-full px-2`}
                      >
                        <div className="">
                          <div className="pdf-only">
                            <img src={logo} alt="Logo" className="w-32" />
                          </div>
                          <h1
                            className={`${
                              isGeneratingPDF ? "text-2xl" : "text-xl"
                            } font-bold text-[#194E5B]`}
                          >
                            Managed Marketing Report
                          </h1>
                        </div>
                        <div className="date-display">
                          <div className="d-flex flex-column">
                            <div className="d-flex flex-col items-start justify-start gap-3 align-items-center">
                              <div className="flex gap-2">
                                <ReactDatePicker
                                  className="form-control col-2"
                                  selected={
                                    dateFilter?.startDate
                                      ? moment(dateFilter.startDate).toDate()
                                      : null
                                  }
                                  onChange={(dates) => {
                                    const [start, end] = dates;
                                    if (start === null && end === null) {
                                      const currentDate = moment();
                                      setDateFilter({
                                        startDate: currentDate
                                          .startOf("month")
                                          .format("YYYY-MM-DD"),
                                        endDate: currentDate
                                          .endOf("month")
                                          .format("YYYY-MM-DD"),
                                      });
                                    } else {
                                      setDateFilter({
                                        startDate: start
                                          ? moment(start)
                                              .startOf("month")
                                              .format("YYYY-MM-DD")
                                          : "",
                                        endDate: end
                                          ? moment(end)
                                              .endOf("month")
                                              .format("YYYY-MM-DD")
                                          : "",
                                      });
                                    }
                                  }}
                                  startDate={
                                    dateFilter?.startDate
                                      ? moment(dateFilter.startDate).toDate()
                                      : null
                                  }
                                  endDate={
                                    dateFilter?.endDate
                                      ? moment(dateFilter.endDate).toDate()
                                      : null
                                  }
                                  selectsRange
                                  showMonthYearPicker
                                  dateFormat="MMM/yyyy"
                                  placeholderText="Select date range"
                                  isClearable={
                                    !(
                                      dateFilter?.startDate ===
                                        moment()
                                          .startOf("month")
                                          .format("YYYY-MM-DD") &&
                                      dateFilter?.endDate ===
                                        moment()
                                          .endOf("month")
                                          .format("YYYY-MM-DD")
                                    )
                                  }
                                />
                                {!isPrintMode && (
                                  <div>
                                    <RangePicker
                                      className="pdf-download-button h-[38px] rounded-[10px] "
                                      dropdownClassName="single-calendar"
                                      getPopupContainer={(trigger) =>
                                        trigger.parentElement!
                                      }
                                      value={[
                                        dateFilter.startDate
                                          ? moment(
                                              dateFilter.startDate,
                                              "YYYY-MM-DD"
                                            )
                                          : null,
                                        dateFilter.endDate
                                          ? moment(
                                              dateFilter.endDate,
                                              "YYYY-MM-DD"
                                            )
                                          : null,
                                      ]}
                                      onChange={(dates: any) => {
                                        if (dates && dates[0] && dates[1]) {
                                          setLastUsedDatePicker("range");
                                          setDateFilter({
                                            startDate:
                                              dates[0].format("YYYY-MM-DD"),
                                            endDate:
                                              dates[1].format("YYYY-MM-DD"),
                                          });
                                        } else {
                                          // Reset to current month if cleared
                                          const today = moment();
                                          setLastUsedDatePicker("month");
                                          setDateFilter({
                                            startDate: today
                                              .startOf("month")
                                              .format("YYYY-MM-DD"),
                                            endDate: today
                                              .endOf("month")
                                              .format("YYYY-MM-DD"),
                                          });
                                        }
                                      }}
                                      format="MMM D, YYYY"
                                    />
                                  </div>
                                )}
                                <select
                                  className="form-select max-w-[200px]"
                                  aria-label="Default select example"
                                  value={selectedClinic}
                                  onChange={(e) => {
                                    setSelectedClinic(e.target.value);
                                    sessionStorage.setItem(
                                      "selectedClinic",
                                      e.target.value
                                    );
                                  }}
                                >
                                  {clinics?.map((item: any) => (
                                    <option
                                      value={item.uniqueId}
                                      key={item.uniqueId}
                                    >
                                      {item.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="pdf-only -ml-5">
                                <small className="text-left pt-1 font-semibold text-lg text-[#194E5B] tracking-wide !dashb-filters bg-green-50 px-3 py-1 rounded-md inline-block shadow-sm">
                                  {
                                    clinics?.find(
                                      (item: any) =>
                                        item.uniqueId === selectedClinic
                                    )?.name
                                  }
                                </small>
                              </div>
                            </div>
                            <div className="flex gap-3 mt-2">
                              <small
                                className={`pt-1 text-success dashb-filters ${
                                  isPrintMode
                                    ? "text-[16px] font-semibold"
                                    : "text-[14px]"
                                } text-[14px]`}
                              >
                                From {dateFilter?.startDate} to{" "}
                                {dateFilter?.endDate}
                              </small>
                            </div>
                          </div>
                        </div>
                        {!isPrintMode && !isGeneratingPDF && (
                          <div className="action-buttons flex gap-3">
                            <button
                              className="btn btn-sm text-white rounded-md pdf-download-button"
                              style={{ backgroundColor: "#0E3843" }}
                              onClick={generatePDF}
                              disabled={isGeneratingPDF}
                            >
                              {isGeneratingPDF
                                ? "Generating..."
                                : "Download PDF"}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </header>
                <RankingsSection
                  isPrintMode={isPrintMode}
                  dateFilter={dateFilter}
                  clinicData={
                    clinicData?.uniqueId
                      ? clinicData?.uniqueId
                      : clinics?.[0]?.uniqueId
                  }
                />
                {/* </div>*/}
                <div className="mt-4">
                  <ReviewsSection
                    isPrintMode={isPrintMode}
                    dateFilter={dateFilter}
                    clinicData={
                      clinicData?.uniqueId
                        ? clinicData?.uniqueId
                        : clinics?.[0]?.uniqueId
                    }
                  />
                </div>
              </div>
              <div className="section">
                {/* <AnalyticsSection
                  isPrintMode={isPrintMode}
                  clinicUniqueId={
                    clinicData?.uniqueId
                      ? clinicData?.uniqueId
                      : clinics?.[0]?.uniqueId
                  }
                  startDate={dateFilter?.startDate}
                  endDate={dateFilter?.endDate}
                /> */}
                {/* </div>
              <div className="section"> */}
                {/* <EmailCampaignsSection isPrintMode={isPrintMode} /> */}
                <AcquisitionSection
                  isPrintMode={isPrintMode}
                  dateFilter={dateFilter}
                  clinicData={
                    clinicData?.uniqueId
                      ? clinicData?.uniqueId
                      : clinics?.[0]?.uniqueId
                  }
                />
              </div>
              <div className="section">
                <LostLeadsSection
                  isPrintMode={isPrintMode}
                  dateFilter={dateFilter}
                  clinicData={
                    clinicData?.uniqueId
                      ? clinicData?.uniqueId
                      : clinics?.[0]?.uniqueId
                  }
                />
              </div>
              <div className="section">
                <NewPatientsSection
                  isPrintMode={isPrintMode}
                  dateFilter={dateFilter}
                  clinicUniqueId={
                    clinicData?.uniqueId
                      ? clinicData?.uniqueId
                      : clinics?.[0]?.uniqueId
                  }
                />
              </div>
              <div className="section">
                <PatientFollowUp
                  isPrintMode={isPrintMode}
                  dateFilter={dateFilter}
                  clinicUniqueId={
                    clinicData?.uniqueId
                      ? clinicData?.uniqueId
                      : clinics?.[0]?.uniqueId
                  }
                />
              </div>
              {/* <div className="section">
                <PatientCallBack
                  isPrintMode={isPrintMode}
                  dateFilter={dateFilter}
                  clinicUniqueId={
                    clinicData?.uniqueId
                      ? clinicData?.uniqueId
                      : clinics?.[0]?.uniqueId
                  }
                />
              </div> */}
              <div className="section">
                <ObservationsRecommendationsSection
                  isPrintMode={isPrintMode}
                  dateFilter={dateFilter}
                  clinicData={
                    clinicData?.uniqueId
                      ? clinicData?.uniqueId
                      : clinics?.[0]?.uniqueId
                  }
                />
              </div>
            </div>
          </main>
          <footer className="bg-gray-100 py-4 mt-6 text-center text-gray-600 text-sm">
            <div className="">
              <p>
                &copy; 2025 Xcare Managed Marketing Report. All rights reserved.
              </p>
            </div>
          </footer>
        </>
      )}
    </div>
  );
};

export default ClientReport;
